import type { LegalStructure } from '../../config/i18nConfig'
import type { LegalPersonMutationDto } from '../config/partyMutationConfig'
import { formatPostalCode } from './postalCodeHelper'
import { resolveKvkCountry } from './kvkCountryTable'

/**
 * Mapping van de KvK Handelsregister-antwoorden naar onze partijvelden. Bewust een pure module
 * zonder React: alleen zo is hij te unit-testen (testbestanden in dit project mogen geen componenten
 * importeren). De opzoekfuncties komen als argument binnen.
 */

export type KvkSearchResult = {
    number: string
    name: string
    street: string
    locality: string
    branchNumber?: string
    type: string
}

type KvkSearchBaseAddress = {
    type: 'bezoekadres' | 'correspondentieadres'
}

type KvkSearchDutchAddress = KvkSearchBaseAddress & {
    straatnaam?: string
    huisnummer?: number
    huisletter?: string
    postbusnummer?: number
    postcode?: string
    plaats?: string
}

type KvkSearchForeignAddress = KvkSearchBaseAddress & {
    straatHuisnummer?: string
    postcodeWoonplaats?: string
    land?: string
}

type KvkSearchAddress = {
    binnenlandsAdres?: KvkSearchDutchAddress
    buitenlandsAdres?: KvkSearchForeignAddress
}

type KvkSearchPageResult = {
    kvkNummer: string
    vestigingsnummer?: string
    naam: string
    adres?: KvkSearchAddress
    type: 'hoofdvestiging' | 'nevenvestiging' | 'rechtspersoon' | string
}

export type KvkSearchPage = {
    pagina: number
    resultatenPerPagina: number
    totaal: number
    resultaten: KvkSearchPageResult[]
}

type KvkLegalStructures =
    | 'Besloten Vennootschap'
    | 'Naamloze Vennootschap'
    | 'Eenmanszaak'
    | 'Stichting'
    | 'Vennootschap Onder Firma'
    | 'Maatschap'
    | 'Vereniging'
    | 'Commanditaire Vennootschap'
    | 'Coöperatie'
    | 'Publiekrechtelijke Rechtspersoon'
    | string

/**
 * Eén adresvorm voor basis- en vestigingsprofiel. De losse velden zijn voor Nederlandse adressen;
 * bij een buitenlands adres vult KvK in plaats daarvan `straatHuisnummer` en `postcodeWoonplaats`.
 */
export type KvkProfileAddress = {
    type: 'bezoekadres' | 'correspondentieadres'
    postcode?: string
    plaats?: string
    land?: string
    postbusnummer?: string
    straatnaam?: string
    huisnummer?: string
    huisletter?: string
    huisnummerToevoeging?: string
    straatHuisnummer?: string
    postcodeWoonplaats?: string
    regio?: string
}

export type KvkProfilePage = {
    kvkNummer: string
    naam: string
    _embedded?: {
        hoofdvestiging?: {
            adressen?: KvkProfileAddress[]
        }
        eigenaar?: {
            rechtsvorm?: KvkLegalStructures
            adressen?: KvkProfileAddress[]
        }
    }
}

export type KvkBranchProfilePage = {
    vestigingsnummer: string
    kvkNummer: string
    adressen?: KvkProfileAddress[]
}

type MappedAddress = {
    street: string
    number: string
    postalCode: string
    locality: string
    country?: string
}

/** Aantal cijfers in de postcode van landen waar die volledig numeriek is en een vaste lengte heeft. */
const fixedLengthPostalCodes: Record<string, number> = { BE: 4, CH: 4, DE: 5, FR: 5 }

/**
 * De Britse postcode is niet numeriek en bevat zelf een spatie ("SW1A 1AA"), dus die is niet op
 * lengte te knippen. Het formaat ligt wel vast: een outward code, dan een inward code van precies
 * één cijfer en twee letters. Daarop kunnen we exact matchen.
 */
const britishPostalCode = /^\s*(?:GB-)?([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\s+(\S.*)$/i

/**
 * Splitst `postcodeWoonplaats` ("D-51643 Gummersbach") in postcode en plaats. Dat kan alleen waar het
 * postcodeformaat vastligt, anders valt niet te zien waar de plaatsnaam begint. Een landprefix mag
 * blijven staan: {@link formatPostalCode} stript die eruit. Past het patroon niet, dan geeft dit
 * niets terug en blijft de hele string als plaatsnaam staan.
 */
const splitPostalCodeLocality = (value: string, country?: string): { postalCode?: string; locality?: string } => {
    const digits = fixedLengthPostalCodes[country]
    const pattern = country === 'GB' ? britishPostalCode : digits && new RegExp(`^\\s*(?:[A-Za-z]{1,3}-)?(\\d{${digits}})\\s+(\\S.*)$`)
    const match = pattern && value.match(pattern)

    return match ? { postalCode: match[1].toUpperCase().replace(/\s+/g, ' ').trim(), locality: match[2].trim() } : {}
}

/**
 * Het land bepaalt hoe de postcode wordt geformatteerd, dus het wordt eerst opgelost. Een leeg
 * `land` betekent een Nederlands adres; een landnaam die niet in de BRP-tabel staat levert
 * `undefined` op, waarna het formulier de gebruiker om een land vraagt.
 */
const mapAddress = (address: KvkProfileAddress): MappedAddress => {
    const country = address.land ? resolveKvkCountry(address.land) : 'NL'

    // Bij een buitenlands adres levert KvK alleen de samengestelde velden.
    if (address.straatHuisnummer || address.postcodeWoonplaats) {
        const split = splitPostalCodeLocality(address.postcodeWoonplaats ?? '', country)

        return {
            street: address.straatHuisnummer ?? '',
            number: '',
            postalCode: formatPostalCode(address.postcode ?? split.postalCode, country),
            locality: address.plaats || split.locality || address.postcodeWoonplaats || '',
            country,
        }
    }

    const postalCode = formatPostalCode(address.postcode, country)

    return {
        street: address.postbusnummer ? 'Postbus' : (address.straatnaam ?? ''),
        number: address.postbusnummer ?? `${address.huisnummer ?? ''}${address.huisletter ?? ''}${address.huisnummerToevoeging ?? ''}`,
        postalCode,
        locality: address.plaats ?? '',
        country,
    }
}

// Rechtspersoon boven de vestigingen van hetzelfde KvK-nummer; een rechtspersoon-resultaat heeft geen adres.
const typeOrder: Record<string, number> = { rechtspersoon: 0, hoofdvestiging: 1, nevenvestiging: 2 }

const formatSearchAddress = (address?: KvkSearchAddress): Pick<KvkSearchResult, 'street' | 'locality'> => {
    const dutch = address?.binnenlandsAdres
    const foreign = address?.buitenlandsAdres

    if (dutch) {
        return {
            street: dutch.postbusnummer
                ? `Postbus ${dutch.postbusnummer}`
                : [dutch.straatnaam, `${dutch.huisnummer ?? ''}${dutch.huisletter ?? ''}`].filter(Boolean).join(' ').trim(),
            // De zoek-API scheidt binnen- en buitenland, dus een binnenlands adres is per definitie NL.
            locality: [formatPostalCode(dutch.postcode, 'NL'), dutch.plaats].filter(Boolean).join(' '),
        }
    }

    return {
        street: foreign?.straatHuisnummer ?? '',
        locality: foreign ? [foreign.postcodeWoonplaats, foreign.land].filter(Boolean).join(' ') : '',
    }
}

export const mapSearchResult = (page: KvkSearchPage): KvkSearchResult[] =>
    page.resultaten
        .sort(
            (a: KvkSearchPageResult, b: KvkSearchPageResult) =>
                a.kvkNummer.localeCompare(b.kvkNummer) ||
                (typeOrder[a.type] ?? 9) - (typeOrder[b.type] ?? 9) ||
                (a.vestigingsnummer ?? '').localeCompare(b.vestigingsnummer ?? ''),
        )
        .map<KvkSearchResult>(entry => ({
            number: entry.kvkNummer,
            branchNumber: entry.vestigingsnummer,
            type: entry.type,
            name: entry.naam,
            ...formatSearchAddress(entry.adres),
        }))
        .reduce(
            (acc: KvkSearchResult[], current: KvkSearchResult) =>
                acc.find(entry => entry.number === current.number && entry.branchNumber === current.branchNumber) ? acc : [...acc, current],
            [],
        )

export const mapProfileResult = (
    getLegalStructureByName: (name: string) => LegalStructure,
    page: KvkProfilePage,
): Partial<LegalPersonMutationDto> => {
    const addresses = [...(page._embedded?.hoofdvestiging?.adressen || []), ...(page._embedded?.eigenaar?.adressen || [])]
    const address = addresses.find(entry => entry.type === 'bezoekadres')
    const poBoxAddress = addresses.find(entry => entry.type === 'correspondentieadres')

    const mapped = address && mapAddress(address)
    const mappedPoBox = poBoxAddress && mapAddress(poBoxAddress)
    const rechtsvorm = page._embedded?.eigenaar?.rechtsvorm

    return {
        cocNumber: page.kvkNummer,
        name: page.naam,
        // De KvK kent rechtsvormen die wij niet als aparte waarde voeren, vooral bij buitenlandse
        // bedrijven ("Kapitaalvennootschap binnen EER"). Die vallen terug op Overig.
        legalStructure: rechtsvorm ? (getLegalStructureByName(rechtsvorm)?.alpha ?? 'OTHER') : undefined,
        addressStreet: mapped?.street,
        addressNumber: mapped?.number,
        addressPostalCode: mapped?.postalCode,
        addressLocality: mapped?.locality,
        addressCountry: mapped?.country,
        poBoxAddressStreet: mappedPoBox?.street,
        poBoxAddressNumber: mappedPoBox?.number,
        poBoxAddressPostalCode: mappedPoBox?.postalCode,
        poBoxAddressLocality: mappedPoBox?.locality,
        poBoxAddressCountry: mappedPoBox?.country,
    }
}

/**
 * Maps the branch profile (vestigingsprofiel) to overrides on top of the base profile:
 * the branch number and, when present, the addresses of the specific branch.
 */
export const mapBranchProfileResult = (page: KvkBranchProfilePage): Partial<LegalPersonMutationDto> => {
    const address = page.adressen?.find(entry => entry.type === 'bezoekadres')
    const poBoxAddress = page.adressen?.find(entry => entry.type === 'correspondentieadres')

    const mapped = address && mapAddress(address)
    const mappedPoBox = poBoxAddress && mapAddress(poBoxAddress)

    return {
        cocBranchNumber: page.vestigingsnummer,
        ...(mapped
            ? {
                  addressStreet: mapped.street,
                  addressNumber: mapped.number,
                  addressPostalCode: mapped.postalCode,
                  addressLocality: mapped.locality,
                  addressCountry: mapped.country,
              }
            : {}),
        ...(mappedPoBox
            ? {
                  poBoxAddressStreet: mappedPoBox.street,
                  poBoxAddressNumber: mappedPoBox.number,
                  poBoxAddressPostalCode: mappedPoBox.postalCode,
                  poBoxAddressLocality: mappedPoBox.locality,
                  poBoxAddressCountry: mappedPoBox.country,
              }
            : {}),
    }
}
